<template>
	<PointsHistoryDialog v-model="open" :overlay-target="overlayTarget" :filter="filter" />
	<v-snackbar v-model="earnedSnackbar" :timeout="5000" rounded="xl" color="success">
		{{ t('YouEarn') + ' ' + earnedPoints + ' ' + t('Points').toLowerCase() }}
	</v-snackbar>
</template>

<script setup>
import { ref, inject, onMounted, onBeforeUnmount } from 'vue';
import PointsHistoryDialog from './PointsHistoryDialog.vue';
import { fireConfettiBurst } from '@/utils/ui/confettiEffects';
import { eventBus } from '@/utils/core/eventBus';
import { reconcilePendingClaims } from '@/api/sales/points';
import { log } from '@/utils/core/vueLogger';
import { useTranslator } from '@/composables/useTranslator';

const t = useTranslator();
const overlayTarget = inject('overlayTarget', null);
const open = ref(false);
const filter = ref(null);
const earnedSnackbar = ref(false);
const earnedPoints = ref(0);

function handleOpen(event) {
	filter.value = event?.detail ?? null;
	open.value = true;
}

// RG #22908: fires regardless of which view/dialog is currently open — this zone is
// persistent (mounted once right after main sync, never torn down by view changes).
function handleScoreClaimValidated(data) {
	fireConfettiBurst(0.5);
	earnedPoints.value = data?.pointsValidated ?? 0;
	earnedSnackbar.value = true;
}

// RG #22908: closing a visit unmounts the non-persistent vue-points-statement-zone (via
// evtChangeView -> unmountAll) before the deferred 'appointments-updated' event for that same
// visit arrives, which otherwise drops any points claim submitted just before closing. This zone
// is persistent, so it's guaranteed to still be around to pick up the claim.
function handleAppointmentsUpdated(data) {
	log('info', 'Points', `🎯[CLAIM-DEBUG] PointsDialogZone got appointments-updated — reconciling`);
	reconcilePendingClaims(data);
}

onMounted(() => {
	window.addEventListener('open-vue-points-dialog', handleOpen);
	eventBus.on('score-claim-validated', handleScoreClaimValidated);
	eventBus.on('appointments-updated', handleAppointmentsUpdated);
	log('info', 'Points', `🎯[CLAIM-DEBUG] PointsDialogZone mounted — listening for appointments-updated`);
});

onBeforeUnmount(() => {
	window.removeEventListener('open-vue-points-dialog', handleOpen);
	eventBus.off('score-claim-validated', handleScoreClaimValidated);
	eventBus.off('appointments-updated', handleAppointmentsUpdated);
});
</script>
